import { useEffect, useState, useCallback } from 'react';
import { Loader2, Plus, Trash2, Save, X, Clock, Globe, Gift } from 'lucide-react';
import {
  getMonsterAttack, saveAttackRound, deleteAttackRound, saveAttackWorlds, saveAttackPool, deleteAttackPool,
  type MonsterAttackResponse, type AttackRound, type AttackPoolItem, type EventSummary, type GameItem,
} from '@/shared/services/eventManagerService';
import { useToast } from '@/shared/contexts/ToastContext';
import { GameItemPicker, ItemKindInline } from './GameItem';
import { worldName } from '@/features/Notifications/worldNames';

const pad = (n: number) => String(n).padStart(2, '0');
const emptyRound = (orderId: number): AttackRound => ({
  orderId, noticeStartHour: 20, noticeStartMinute: 0, noticeTime: 1, attackTime: 10,
  spawnMonsterIndex: 0, authorityType: 0, perfectDrop: 0, multiDropType: 0,
  multiDropMin: 0, multiDropMax: 0, dropPoolId: 0,
});

export default function MonsterAttackEditor({ event }: { event: EventSummary }) {
  const { notify } = useToast();
  const id = event.eventId;
  const [data, setData] = useState<MonsterAttackResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await getMonsterAttack(id)); }
    catch (err: any) { notify('error', err?.detail || err?.message || 'Erro'); }
    finally { setLoading(false); }
  }, [id, notify]);
  useEffect(() => { load(); }, [load]);

  if (loading || !data) return <div className="py-16 flex justify-center text-white/40"><Loader2 size={20} className="animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <WorldsSection eventId={id} worlds={data.worlds} onChanged={load} />
      <RoundsSection eventId={id} data={data} onChanged={load} />
      <PoolsSection eventId={id} data={data} onChanged={load} />
    </div>
  );
}

/* ------------------------------------------------ worlds */
function WorldsSection({ eventId, worlds, onChanged }: { eventId: number; worlds: number[]; onChanged: () => void }) {
  const { notify } = useToast();
  const [list, setList] = useState<number[]>(worlds);
  const [add, setAdd] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setList(worlds); }, [worlds.join(',')]);

  const addWorld = () => {
    const n = Number(add);
    if (Number.isInteger(n) && n > 0 && !list.includes(n)) setList([...list, n].sort((a, b) => a - b));
    setAdd('');
  };
  const save = async () => {
    setBusy(true);
    try { await saveAttackWorlds(eventId, list); notify('success', 'Worlds salvos'); onChanged(); }
    catch (err: any) { notify('error', err?.detail || err?.message || 'Erro'); }
    finally { setBusy(false); }
  };

  return (
    <div className="surface-card rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-bold text-white uppercase tracking-wide flex items-center gap-2"><Globe size={15} className="text-rose-400" /> Worlds</h2>
        <button onClick={save} disabled={busy} className="flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-semibold btn-primary cursor-pointer disabled:opacity-40">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Salvar
        </button>
      </div>
      <div className="flex flex-wrap gap-2 mb-3">
        {list.length === 0 && <span className="text-xs text-white/40">Nenhum world selecionado</span>}
        {list.map(w => (
          <span key={w} className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-lg bg-[#00e1d7]/10 border border-[#00e1d7]/25 text-xs text-white">
            <span className="font-mono text-[#00e1d7]">{w}</span> {worldName(w)}
            <button onClick={() => setList(list.filter(x => x !== w))} className="text-white/40 hover:text-red-400 cursor-pointer"><X size={12} /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input value={add} onChange={(e) => setAdd(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addWorld()} placeholder="World ID..."
          className="w-32 bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-1.5 text-sm text-white font-mono" />
        <button onClick={addWorld} className="px-3 py-1.5 rounded-lg bg-white/[0.06] text-white/80 hover:bg-white/[0.1] text-sm cursor-pointer flex items-center gap-1"><Plus size={14} /> Add</button>
      </div>
    </div>
  );
}

/* ------------------------------------------------ rounds timeline */
function RoundsSection({ eventId, data, onChanged }: { eventId: number; data: MonsterAttackResponse; onChanged: () => void }) {
  const { notify } = useToast();
  const nextOrder = data.rounds.length ? Math.max(...data.rounds.map(r => r.orderId)) + 1 : 0;
  const [editing, setEditing] = useState<AttackRound | null>(null);

  const del = async (orderId: number) => {
    if (!confirm(`Remover round #${orderId}?`)) return;
    try { await deleteAttackRound(eventId, orderId); notify('success', 'Round removido'); onChanged(); }
    catch (err: any) { notify('error', err?.detail || err?.message || 'Erro'); }
  };

  return (
    <div className="surface-card rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-bold text-white uppercase tracking-wide flex items-center gap-2"><Clock size={15} className="text-rose-400" /> Timeline</h2>
        <button onClick={() => setEditing(emptyRound(nextOrder))} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold btn-primary cursor-pointer"><Plus size={13} /> Round</button>
      </div>
      {data.rounds.length === 0 ? <p className="text-xs text-white/40 py-6 text-center">Nenhum round. Adicione horários de invasão.</p> : (
        <div className="space-y-2">
          {data.rounds.map(r => (
            <div key={r.orderId} className="flex items-center gap-4 p-3 rounded-lg border border-white/[0.06] hover:bg-white/[0.02] transition">
              <div className="text-center shrink-0 w-16">
                <p className="text-lg font-bold text-white tabular-nums">{pad(r.noticeStartHour)}:{pad(r.noticeStartMinute)}</p>
                <p className="text-[9px] text-white/40 uppercase">Round #{r.orderId}</p>
              </div>
              <div className="flex-1 min-w-0 text-xs text-white/70">
                <p>Mob <span className="font-mono text-white">#{r.spawnMonsterIndex}</span></p>
                <p className="text-white/45 mt-0.5">Notice {r.noticeTime} · Attack {r.attackTime} · Pool #{r.dropPoolId}</p>
              </div>
              <div className="flex gap-1 shrink-0">
                <button onClick={() => setEditing(r)} className="px-2.5 py-1 rounded-md bg-white/[0.06] text-white/75 hover:bg-white/[0.1] text-xs cursor-pointer">Editar</button>
                <button onClick={() => del(r.orderId)} className="p-1.5 rounded-md text-red-400/80 hover:text-red-400 hover:bg-red-500/10 cursor-pointer"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      {editing && <RoundModal eventId={eventId} round={editing} onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); await onChanged(); }} />}
    </div>
  );
}

function RoundModal({ eventId, round, onClose, onSaved }: { eventId: number; round: AttackRound; onClose: () => void; onSaved: () => void }) {
  const { notify } = useToast();
  const [r, setR] = useState<AttackRound>(round);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof AttackRound, v: any) => setR(p => ({ ...p, [k]: v }));
  const save = async () => {
    setBusy(true);
    try { await saveAttackRound(eventId, r.orderId, r); notify('success', 'Round salvo'); onSaved(); }
    catch (err: any) { notify('error', err?.detail || err?.message || 'Erro'); setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="surface-card rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4"><h2 className="text-lg font-bold text-white">Round #{r.orderId}</h2><button onClick={onClose} className="text-white/55 hover:text-white cursor-pointer"><X size={18} /></button></div>
        <div className="grid grid-cols-2 gap-3">
          <Lbl label="Hora início"><input type="number" min={0} max={23} value={r.noticeStartHour} onChange={(e) => set('noticeStartHour', Number(e.target.value))} className={`${inp} font-mono`} /></Lbl>
          <Lbl label="Minuto início"><input type="number" min={0} max={59} value={r.noticeStartMinute} onChange={(e) => set('noticeStartMinute', Number(e.target.value))} className={`${inp} font-mono`} /></Lbl>
          <Lbl label="Notice Time"><input type="number" value={r.noticeTime} onChange={(e) => set('noticeTime', Number(e.target.value))} className={`${inp} font-mono`} /></Lbl>
          <Lbl label="Attack Time"><input type="number" value={r.attackTime} onChange={(e) => set('attackTime', Number(e.target.value))} className={`${inp} font-mono`} /></Lbl>
          <Lbl label="Spawn Monster Idx"><input type="number" value={r.spawnMonsterIndex} onChange={(e) => set('spawnMonsterIndex', Number(e.target.value))} className={`${inp} font-mono`} /></Lbl>
          <Lbl label="Drop Pool ID"><input type="number" value={r.dropPoolId} onChange={(e) => set('dropPoolId', Number(e.target.value))} className={`${inp} font-mono`} /></Lbl>
        </div>
        <details className="mt-3"><summary className="text-xs text-white/50 cursor-pointer">Avançado (drop)</summary>
          <div className="grid grid-cols-3 gap-2 mt-2">
            <Lbl label="Authority"><input type="number" value={r.authorityType} onChange={(e) => set('authorityType', Number(e.target.value))} className={`${inp} font-mono`} /></Lbl>
            <Lbl label="Perfect"><input type="number" value={r.perfectDrop} onChange={(e) => set('perfectDrop', Number(e.target.value))} className={`${inp} font-mono`} /></Lbl>
            <Lbl label="MultiType"><input type="number" value={r.multiDropType} onChange={(e) => set('multiDropType', Number(e.target.value))} className={`${inp} font-mono`} /></Lbl>
            <Lbl label="MultiMin"><input type="number" value={r.multiDropMin} onChange={(e) => set('multiDropMin', Number(e.target.value))} className={`${inp} font-mono`} /></Lbl>
            <Lbl label="MultiMax"><input type="number" value={r.multiDropMax} onChange={(e) => set('multiDropMax', Number(e.target.value))} className={`${inp} font-mono`} /></Lbl>
          </div>
        </details>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-lg bg-white/[0.04] text-white/75 hover:bg-white/[0.08] text-sm cursor-pointer">Cancelar</button>
          <button onClick={save} disabled={busy} className="flex-1 py-2.5 rounded-lg btn-primary font-bold text-sm cursor-pointer disabled:opacity-40 flex items-center justify-center gap-2">
            {busy && <Loader2 size={14} className="animate-spin" />} Salvar
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------ reward pools (event-scoped) */
function PoolsSection({ eventId, data, onChanged }: { eventId: number; data: MonsterAttackResponse; onChanged: () => void }) {
  const { notify } = useToast();
  const poolIds = Object.keys(data.pools).map(Number).sort((a, b) => a - b);
  const nextId = poolIds.length ? Math.max(...poolIds) + 1 : 0;

  return (
    <div className="surface-card rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-bold text-white uppercase tracking-wide flex items-center gap-2"><Gift size={15} className="text-rose-400" /> Reward Pools</h2>
        <button onClick={() => onChanged()} className="text-xs text-white/40 hover:text-white/70 cursor-pointer">recarregar</button>
      </div>
      <div className="space-y-4">
        {poolIds.map(pid => (
          <AttackPool key={pid} eventId={eventId} poolId={pid} items={data.pools[String(pid)]} name={data.poolNames[String(pid)] ?? ''} onChanged={onChanged} />
        ))}
        <AttackPool key={`new-${nextId}`} eventId={eventId} poolId={nextId} items={[]} name="" isNew onChanged={onChanged} />
      </div>
    </div>
  );
}

function AttackPool({ eventId, poolId, items, name, isNew, onChanged }: {
  eventId: number; poolId: number; items: AttackPoolItem[]; name: string; isNew?: boolean; onChanged: () => void;
}) {
  const { notify } = useToast();
  const [pid, setPid] = useState(poolId);
  const [list, setList] = useState<AttackPoolItem[]>(items);
  const [pname, setPname] = useState(name);
  const [busy, setBusy] = useState(false);
  const [pick, setPick] = useState(false);

  const add = (it: GameItem) => setList([...list, { order: list.length, itemKind: it.itemId, itemOption: '0', itemPeriod: 0, ratio: 1 }]);
  const upd = (i: number, patch: Partial<AttackPoolItem>) => setList(list.map((p, j) => j === i ? { ...p, ...patch } : p));
  const rm = (i: number) => setList(list.filter((_, j) => j !== i));
  const save = async () => {
    if (isNew && list.length === 0) { notify('error', 'Adicione ao menos 1 item'); return; }
    setBusy(true);
    try { await saveAttackPool(eventId, pid, { poolId: pid, name: pname || undefined, items: list }); notify('success', `Pool #${pid} salvo`); onChanged(); }
    catch (err: any) { notify('error', err?.detail || err?.message || 'Erro'); }
    finally { setBusy(false); }
  };
  const del = async () => {
    if (!confirm(`Remover o pool #${pid}?`)) return;
    try { await deleteAttackPool(eventId, pid); notify('success', 'Pool removido'); onChanged(); }
    catch (err: any) { notify('error', err?.detail || err?.message || 'Erro'); }
  };

  return (
    <div className={`rounded-xl border p-4 ${isNew ? 'border-dashed border-white/[0.1]' : 'border-white/[0.07] bg-[#0b1119]'}`}>
      <div className="flex items-center gap-2 mb-3">
        {isNew ? (
          <><span className="text-xs text-white/50">Novo pool #</span>
            <input type="number" value={pid} onChange={(e) => setPid(Number(e.target.value))} className="w-16 bg-[#0d141b] border border-white/[0.06] rounded px-2 py-1 text-xs text-white font-mono" /></>
        ) : <span className="text-sm font-bold text-white">Pool #{pid}</span>}
        <input value={pname} onChange={(e) => setPname(e.target.value)} placeholder="nome" className="flex-1 bg-[#0d141b] border border-white/[0.06] rounded px-2 py-1 text-xs text-white" />
        {!isNew && <button onClick={del} className="text-red-400/80 hover:text-red-400 cursor-pointer"><Trash2 size={14} /></button>}
      </div>
      <div className="space-y-1.5">
        {list.map((it, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <ItemKindInline value={it.itemKind} onChange={(v) => upd(i, { itemKind: v })} />
            <input value={it.itemOption} onChange={(e) => upd(i, { itemOption: e.target.value })} title="option" className="w-14 bg-[#0d141b] border border-white/[0.06] rounded px-1.5 py-1 text-[10px] text-white font-mono shrink-0" />
            <input type="number" value={it.itemPeriod} onChange={(e) => upd(i, { itemPeriod: Number(e.target.value) })} title="period" className="w-12 bg-[#0d141b] border border-white/[0.06] rounded px-1.5 py-1 text-[10px] text-white font-mono shrink-0" />
            <input type="number" step="0.1" value={it.ratio} onChange={(e) => upd(i, { ratio: Number(e.target.value) })} title="ratio" className="w-14 bg-[#0d141b] border border-white/[0.06] rounded px-1.5 py-1 text-xs text-white font-mono shrink-0" />
            <button onClick={() => rm(i)} className="text-white/40 hover:text-red-400 cursor-pointer shrink-0"><X size={13} /></button>
          </div>
        ))}
        <div className="flex gap-2">
          <button onClick={() => setPick(true)} className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg border border-dashed border-white/[0.1] text-[11px] text-white/50 hover:text-white cursor-pointer"><Plus size={13} /> Item</button>
          <button onClick={save} disabled={busy} className="px-4 py-1.5 rounded-lg btn-primary font-bold text-xs cursor-pointer disabled:opacity-40 flex items-center gap-1.5">{busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Salvar</button>
        </div>
      </div>
      <GameItemPicker open={pick} onClose={() => setPick(false)} title="Item do reward pool" onPick={add} />
    </div>
  );
}

const inp = 'w-full bg-[#0d141b] border border-white/[0.06] rounded-lg px-2.5 py-1.5 text-sm text-white';
function Lbl({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><label className="text-[10px] text-white/50 mb-1 block">{label}</label>{children}</div>;
}
